import { useState } from 'react';
import DataTable from '../../components/DataTable';
import Pagination from '../../components/Pagination';
import SearchBar from '../../components/SearchBar';
import ConfirmDialog from '../../components/ConfirmDialog';
import Modal from '../../components/Modal';
import { useToast } from '../../context/ToastContext';
import { itinerariesHooks, parseJson, detailOf } from './itinerariesConfig';

const LIMIT = 20;

// Pretty-print a JSON string / object; fall back to the raw value.
function pretty(v) {
  const parsed = parseJson(v);
  if (parsed != null) return JSON.stringify(parsed, null, 2);
  return typeof v === 'string' ? v : '—';
}

export default function ItinerariesPage() {
  const toast = useToast();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [viewing, setViewing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const { data, isLoading, error } = itinerariesHooks.useList({ page, limit: LIMIT, search });
  const rows = data?.data || [];

  const delM = itinerariesHooks.useRemove({
    onSuccess: () => { toast.success('Deleted'); setDeleting(null); },
    onError: (e) => toast.error(e.message || 'Delete failed'),
  });

  const columns = [
    {
      key: 'name', header: 'Name', render: (r) => {
        const det = detailOf(r);
        return det?.name || <span className="muted">#{r.id}</span>;
      },
    },
    { key: 'input_token', header: 'Input token', render: (r) => r.input_token ?? '—' },
    { key: 'output_token', header: 'Output token', render: (r) => r.output_token ?? '—' },
  ];

  return (
    <div className="page">
      <div className="page__header"><h1>Itineraries</h1></div>
      <div className="page__toolbar">
        <SearchBar value={search} onSearch={(v) => { setSearch(v); setPage(1); }} placeholder="Search itineraries…" />
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        isLoading={isLoading}
        error={error}
        emptyText="No itineraries yet."
        actions={(row) => (
          <>
            <button className="btn btn--sm" onClick={() => setViewing(row)}>View</button>
            <button className="btn btn--sm btn--danger" onClick={() => setDeleting(row)}>Delete</button>
          </>
        )}
      />

      {data && <Pagination page={page} limit={LIMIT} total={data.total} onPage={setPage} />}

      <Modal
        open={!!viewing}
        size="wide"
        title={viewing ? (detailOf(viewing)?.name || `Itinerary #${viewing.id}`) : ''}
        onClose={() => setViewing(null)}
      >
        {viewing && (
          <div className="json-split">
            <div className="json-split__pane">
              <h3 className="json-split__title">Request</h3>
              <pre className="json-block">{pretty(viewing.request_json)}</pre>
            </div>
            <div className="json-split__pane">
              <h3 className="json-split__title">Response</h3>
              <pre className="json-block">{pretty(viewing.response_json)}</pre>
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={!!deleting}
        message="Delete this itinerary? This cannot be undone."
        pending={delM.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={() => delM.mutate(deleting.id)}
      />
    </div>
  );
}
